import { faDownToLine, faPieChart } from '@fortawesome/pro-light-svg-icons';
import { faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { orderBy } from 'lodash';
import { useMemo, useState } from 'react';
import CheckPermissions from '@/components/CheckPermissions';
import DonutChart, {
  type DonutChartDataTypes
} from '@/components/charts/DonutChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import type { QBFilter, QBFilterOptions } from '@/components/QueryBuilder';
import {
  qbFiltersAtom,
  qbTimeFilterAtom,
  useQBFilterActions,
  useQueryBuilderParams
} from '@/components/QueryBuilder/QueryBuilder.hooks';
import { QueryBuilderBar } from '@/components/QueryBuilder/QueryBuilderBar';
import { PERMISSIONS } from '@/constants/auth.constants';
import {
  PRIORITY_COLORS,
  STATUS_COLORS
} from '@/constants/chartcolors.constants';
import { TICKET_FILTERS_STORAGE_KEY } from '@/constants/common';
import {
  mergeSearchableSelectFieldOptions,
  mergeSearchableSelectOptions
} from '@/features/SearchBar';
import { TicketExportDialog } from '@/features/TicketExportDialog';
import TicketsTableV2 from '@/features/TicketTable/TicketsTableV2';
import usePermissionsCheck from '@/hooks/usePermissionsCheck.tsx';
import Header from '@/layout/Header/Header';
import { MainContent } from '@/layout/MainContent';
import { queryClient } from '@/services/BaseApi.service';

import { selectedOrganizationAtom } from '../Organizations';
import {
  CUSTOMER_FACING_TICKET_STATUSES,
  TICKET_GROUP_BY_FIELDS,
  TICKET_STATUS,
  TICKETS_SEARCHABLE_SELECT_FILTERS
} from './Tickets.constants';
import {
  clearTicketsSearchCache,
  mapFiltersToRequestParams,
  ticketsGridApiAtom,
  ticketWidgetsCollapsedAtom,
  useFetchGroupByOptions,
  useFetchTicketEntities,
  useFetchTicketFilters
} from './Tickets.store';
import useStyles from './Tickets.styles';
import {
  mapGroupByOptionsToDonutData,
  normalizeCustomerTicketAuthorFilters
} from './Tickets.utils';

export const Tickets = () => {
  const ticketGridApi = useAtomValue(ticketsGridApiAtom);

  const [widgetsCollapsed, setWidgetsCollapsed] = useAtom(
    ticketWidgetsCollapsedAtom
  );
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const [ticketFilters, setTicketFilters] = useAtom(qbFiltersAtom);
  const [ticketTimeFilter, setTicketTimeFilter] = useAtom(qbTimeFilterAtom);
  const userIsInternal = usePermissionsCheck('internal');
  const normalizeFilters = (filters: QBFilter[]) =>
    normalizeCustomerTicketAuthorFilters(filters, userIsInternal);
  const { updateFilters, removeFilter } = useQBFilterActions(
    `${TICKET_FILTERS_STORAGE_KEY}V2`,
    normalizeFilters
  );

  const { data: ticketFiltersConfig, isLoading: isLoadingTicketFilters } =
    useFetchTicketFilters();

  const organizationParam = { organization__label: selectedOrganization.label };

  let requestParams = mapFiltersToRequestParams(
    ticketFilters || [],
    ticketTimeFilter,
    'published_at'
  );

  if (selectedOrganization.id > 0) {
    requestParams = { ...requestParams, ...organizationParam };
  }

  const { isReady } = useQueryBuilderParams({
    config: ticketFiltersConfig || {},
    isLoading: isLoadingTicketFilters,
    sessionKey: `${TICKET_FILTERS_STORAGE_KEY}V2`,
    normalizeFilters,
    defaultFilter: [
      {
        field: 'ticket_status__name',
        operator: '__in',
        value: 'Open,Acknowledged'
      }
    ],
    setFilters: setTicketFilters,
    setTimeFilter: setTicketTimeFilter
  });

  const { data: groupByOptions } = useFetchGroupByOptions(
    TICKET_GROUP_BY_FIELDS,
    requestParams,
    undefined,
    isReady
  );
  const { data: ticketEntities } = useFetchTicketEntities();

  const [ticketExportDialogOpen, setTicketExportDialogOpen] = useState(false);

  const { classes } = useStyles({
    widgetsCollapsed
  });

  const handleChartClick = (value: string, key: string) => {
    const filterExists = (ticketFilters || []).some(
      (filter) =>
        filter.field === key &&
        ((typeof filter.value === 'string' && filter.value === value) ||
          (Array.isArray(filter.value) && filter.value.includes(value)))
    );

    if (filterExists) {
      removeFilter(key);
      return;
    }

    updateFilters([{ field: key, operator: '__eq', value }]);
  };

  const qbOptions = useMemo(
    () =>
      mergeSearchableSelectOptions(
        ticketEntities || {},
        groupByOptions || {}
      ) as Record<string, QBFilterOptions[]>,

    [ticketEntities, groupByOptions]
  );

  // -----------------------------------------------------------------
  const ticketsSearchableFilters = useMemo(() => {
    if (!ticketFiltersConfig || !ticketEntities) return [];

    const searchableSelectFilters = TICKETS_SEARCHABLE_SELECT_FILTERS.map(
      (f) => {
        const filter = { ...f };
        const { field } = filter;
        let { filterOptions } = filter;

        if (qbOptions[field]) {
          filterOptions = mergeSearchableSelectFieldOptions(
            filter.filterOptions,
            qbOptions[field] as QBFilterOptions[]
          );
        }

        if (!userIsInternal) {
          // hide internal filters (owner and team queue) from non-internal users
          if (['team_queue', 'owner__name'].includes(field))
            return { ...filter, hidden: true };

          // hide non-customer facing statuses from non-internal users
          if (field === 'ticket_status__name') {
            filter.filterOptions = filter.filterOptions.filter((item) =>
              CUSTOMER_FACING_TICKET_STATUSES.includes(
                item.value as TICKET_STATUS
              )
            );
          }
        }

        if (userIsInternal && ['user__name'].includes(field)) {
          return { ...filter, hidden: true };
        }

        // TODO: fix this correctly for internal and non internal users
        // hide non-internal filters (author) from internal users
        if (field === 'organization_author' && userIsInternal) {
          return { ...filter, hidden: true };
        }

        // if options are defined in the constants, ensure the sort order is maintained
        if (filter.filterOptions.length > 0) {
          const sortingValues = filter.filterOptions.map(
            (option) => option.value
          );

          filterOptions = filterOptions.sort((a, b) =>
            sortingValues.indexOf(a.value) > sortingValues.indexOf(b.value)
              ? 1
              : -1
          );
        }

        return {
          ...filter,
          filterOptions
        };
      }
    );

    return searchableSelectFilters;
  }, [userIsInternal, qbOptions, ticketFiltersConfig]);

  const priorityGroupByData = useMemo(() => {
    if (!groupByOptions) return [];
    return mapGroupByOptionsToDonutData(
      groupByOptions.ticket_priority__name || [],
      (label) =>
        PRIORITY_COLORS[(label as keyof typeof PRIORITY_COLORS) || 'P3']
    );
  }, [groupByOptions]);

  const statusGroupByData = useMemo(() => {
    if (!groupByOptions) return [];
    return mapGroupByOptionsToDonutData(
      groupByOptions.ticket_status__name || [],
      (label) =>
        STATUS_COLORS[
          (label as keyof typeof STATUS_COLORS) || TICKET_STATUS.OPEN
        ]
    );
  }, [groupByOptions]);

  const categoriesGroupByData = useMemo(() => {
    if (!groupByOptions) return [];
    const categoriesData =
      orderBy(
        groupByOptions.ticket_category__name || [],
        'count',
        'desc'
      ).splice(0, 5) || [];
    return mapGroupByOptionsToDonutData(
      categoriesData,
      (_, index) => `var(--mui-palette-chartColors-${index})`
    );
  }, [groupByOptions]);

  return (
    <>
      <Header
        title="Tickets"
        actions={
          <>
            <CheckPermissions permissions={PERMISSIONS.TICKETS_CREATE}>
              <Button
                variant="outlined"
                startIcon={<FontAwesomeIcon size="lg" icon={faDownToLine} />}
                onClick={() => {
                  setTicketExportDialogOpen(true);
                }}
              >
                Export
              </Button>
            </CheckPermissions>
            <Button
              variant="outlined"
              onClick={() => {
                clearTicketsSearchCache(queryClient);
                ticketGridApi?.refreshServerSide({ purge: true });
              }}
            >
              <FontAwesomeIcon size="lg" icon={faRefresh} />
            </Button>
            <Button
              variant={widgetsCollapsed ? 'outlined' : 'contained'}
              onClick={() => {
                setWidgetsCollapsed(!widgetsCollapsed);
              }}
            >
              <FontAwesomeIcon size="lg" icon={faPieChart} />
            </Button>
          </>
        }
      >
        <Box sx={{ px: 3, pb: 2 }}>
          {ticketFiltersConfig && !!ticketEntities && (
            <QueryBuilderBar
              enableTimeFilter
              config={ticketFiltersConfig}
              options={qbOptions}
              sessionKey={`${TICKET_FILTERS_STORAGE_KEY}V2`}
              searchableSelectFilters={ticketsSearchableFilters}
              filters={ticketFilters || []}
              timeFilter={ticketTimeFilter}
            />
          )}
        </Box>
      </Header>
      <MainContent>
        <div className={classes.ticketsPage}>
          <div className={classes.ticketsPageContent}>
            <div className={classes.ticketsTable}>
              {isReady && <TicketsTableV2 />}
            </div>
            {!widgetsCollapsed && (
              <>
                <div className={classes.ticketsWidget}>
                  <GenericChartWidget title="Tickets by Priority">
                    <DonutChart
                      data={priorityGroupByData as DonutChartDataTypes[]}
                      onChartClick={(value) =>
                        handleChartClick(
                          value.datum.name,
                          'ticket_priority__name'
                        )
                      }
                    />
                  </GenericChartWidget>
                </div>
                <div className={classes.ticketsWidget}>
                  <GenericChartWidget title="Tickets by Status">
                    <DonutChart
                      data={statusGroupByData as DonutChartDataTypes[]}
                      resolveCssVariables
                      onChartClick={(value) =>
                        handleChartClick(
                          value.datum.name,
                          'ticket_status__name'
                        )
                      }
                    />
                  </GenericChartWidget>
                </div>

                <div className={classes.ticketsWidget}>
                  <GenericChartWidget title="Top 5 Ticket Categories">
                    <DonutChart
                      data={categoriesGroupByData as DonutChartDataTypes[]}
                      resolveCssVariables
                      onChartClick={(value) =>
                        handleChartClick(
                          value.datum.name,
                          'ticket_category__name'
                        )
                      }
                    />
                  </GenericChartWidget>
                </div>
              </>
            )}
          </div>
        </div>
        <TicketExportDialog
          handleClose={() => setTicketExportDialogOpen(false)}
          open={ticketExportDialogOpen}
        />
      </MainContent>
    </>
  );
};

export default Tickets;
